/*
  Alverane header: arama şeridi + mega menü. Vanilla JS, kütüphane yok.
  Odak tuzağı için Dawn'ın global.js içindeki trapFocus / removeTrapFocus kullanılır.

  Masaüstü (>= 900px): kolon 1'de hover/odak → kolon 2-3 ve görsel fade ile değişir,
    120ms niyet gecikmesi. Panel genişliği değişmez.
  Mobil (< 900px): alt maddesi olan kategori → alt panel sağdan kayar, kök liste sola çıkar.
    Tarayıcı geri tuşu alt panelden kök listeye döner (history.pushState).
  Her ikisinde: ESC tümünü kapatır, açılınca odak ilk maddeye, kapanınca menü butonuna,
    açıkken body kilitli (scroll konumu korunur), scrim tıklaması kapatır.
*/

const ALV_DESKTOP = window.matchMedia('(min-width: 900px)');
const ALV_HOVER_INTENT_MS = 120;
const ALV_PANEL_MS = 260; // --alv-t-panel (240ms) + pay

class AlvHeader extends HTMLElement {
  connectedCallback() {
    this.search = this.querySelector('.alv-search');
    this.input = this.querySelector('.alv-search__input');
    this.toggles = this.querySelectorAll('[data-alv-search-toggle]');
    this.searchBtn = this.querySelector('.alv-header__search-btn');
    this.toggles.forEach((btn) => btn.addEventListener('click', () => this.toggleSearch()));
    this.search.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeSearch();
    });
    document.addEventListener('click', (e) => {
      if (this.search.hasAttribute('open') && !this.contains(e.target)) this.closeSearch();
    });
  }

  toggleSearch() {
    this.search.hasAttribute('open') ? this.closeSearch() : this.openSearch();
  }

  openSearch() {
    this.search.hidden = false;
    void this.search.offsetWidth; // hidden kalktıktan sonra reflow — geçiş oynasın (rAF arka planda çalışmaz)
    this.search.setAttribute('open', '');
    this.searchBtn.setAttribute('aria-expanded', 'true');
    this.input.focus();
  }

  closeSearch() {
    if (!this.search.hasAttribute('open')) return;
    this.search.removeAttribute('open');
    this.searchBtn.setAttribute('aria-expanded', 'false');
    this.searchBtn.focus();
    clearTimeout(this.searchHideTimer);
    this.searchHideTimer = setTimeout(() => {
      if (!this.search.hasAttribute('open')) this.search.hidden = true;
    }, ALV_PANEL_MS);
  }
}

customElements.define('alv-header', AlvHeader);

class AlvMenu extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-alv-menu-panel]');
    this.text = this.querySelector('.alv-menu__text');
    this.roots = Array.from(this.querySelectorAll('[data-alv-root]'));
    this.images = Array.from(this.querySelectorAll('[data-alv-image]'));
    this.openers = document.querySelectorAll('[data-alv-menu-open]');
    this.opener = this.openers[0];
    this.activeSub = null;
    this.hoverTimer = null;
    this.pushedState = false;

    // Mobilde alt panel açılınca üst satır, kök maddeler ve alt satır sola kayar.
    // <ul>'ü değil tek tek maddeleri kaydırıyoruz: transform'lu bir ata, içindeki
    // alt panelin (position:absolute) containing block'u olur ve onu da kaydırırdı.
    this.shiftTargets = [
      ...this.querySelectorAll('.alv-menu__top, .alv-menu__bottom, .alv-menu__root--mobile'),
    ];
    this.shiftTargets.forEach((el) => el.classList.add('alv-menu__text-shift'));

    // e.detail === 0 → klavye (Enter/Space); fareyle açılınca odak panele gider, kök maddede çerçeve kalmaz
    this.openers.forEach((btn) => btn.addEventListener('click', (e) => this.open(e.detail === 0)));
    this.panel.setAttribute('tabindex', '-1');
    this.querySelectorAll('[data-alv-menu-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      }
    });

    this.roots.forEach((li) => {
      const trigger = li.querySelector('[data-alv-root-trigger]');
      const subOpen = li.querySelector('[data-alv-sub-open]');
      const subClose = li.querySelector('[data-alv-sub-close]');
      const sub = li.querySelector('[data-alv-sub]');

      if (trigger) {
        trigger.addEventListener('mouseenter', () => this.hoverIntent(li));
        trigger.addEventListener('mouseleave', () => this.clearHover());
        trigger.addEventListener('focus', () => this.activate(li));
        if (trigger.tagName === 'BUTTON') trigger.addEventListener('click', () => this.activate(li));
      }
      if (subOpen && sub) subOpen.addEventListener('click', () => this.openSub(li));
      if (subClose) subClose.addEventListener('click', () => this.requestCloseSub());
    });

    window.addEventListener('popstate', (e) => this.onPopState(e));
    ALV_DESKTOP.addEventListener('change', () => {
      if (this.hasAttribute('open')) this.close();
    });

    // Aşama A: açılışta hiçbir kategori aktif değil
    this.dataset.stage = 'a';
  }

  /* ---- açma / kapama ---- */

  open(byKeyboard = false) {
    if (this.hasAttribute('open')) return;
    clearTimeout(this.hideTimer);
    this.resetStage();
    this.hidden = false;
    void this.offsetWidth; // reflow — geçiş oynasın
    document.body.classList.add('overflow-hidden');
    this.openers.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
    this.setAttribute('open', '');
    // Klavyeyle açılınca odak ilk kök maddeye (halka görünür); fareyle açılınca panele (halka yok)
    const first = ALV_DESKTOP.matches
      ? byKeyboard
        ? this.querySelector('.alv-menu__root--desktop')
        : this.panel
      : this.querySelector('.alv-menu__login, .alv-menu__root--mobile');
    trapFocus(this.panel, first || this.panel);
  }

  /* Aşama A: 240px, içerik boş, aktif yok */
  resetStage() {
    clearTimeout(this.expandTimer);
    this.dataset.stage = 'a';
    this.classList.remove('is-expanding');
    this.roots.forEach((r) => r.classList.remove('is-active'));
    this.images.forEach((img) => img.classList.remove('is-active'));
  }

  close() {
    if (!this.hasAttribute('open')) return;
    if (this.activeSub && this.pushedState) {
      // Geri tuşu geçmişini temizle; popstate kapanışı tamamlar
      this.closeAllAfterPop = true;
      history.back();
      return;
    }
    this.finishClose();
  }

  finishClose() {
    this.closeAllAfterPop = false;
    this.clearHover();
    this.resetStage();
    if (this.activeSub) this.closeSub();
    this.removeAttribute('open');
    this.openers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(this.opener);
    // Geçiş bitince gizle; transitionend'e güvenme (reduced-motion / arka plan sekmesi)
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      if (!this.hasAttribute('open')) this.hidden = true;
    }, ALV_PANEL_MS);
  }

  /* ---- masaüstü: hover niyeti ---- */

  hoverIntent(li) {
    if (!ALV_DESKTOP.matches) return;
    this.clearHover();
    this.hoverTimer = setTimeout(() => this.activate(li), ALV_HOVER_INTENT_MS);
  }

  clearHover() {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
  }

  /* Masaüstü: köke gelince aşama B (genişle) ya da daral */
  activate(li) {
    if (!li || !ALV_DESKTOP.matches) return;
    // Her kök paneli genişletir; alt maddesi olmayanda kolonlar boş kalır, yalnızca görsel (referans)
    if (this.dataset.stage !== 'b') {
      this.dataset.stage = 'b';
      this.classList.add('is-expanding');
      clearTimeout(this.expandTimer);
      this.expandTimer = setTimeout(() => this.classList.remove('is-expanding'), 300);
    }
    this.setActive(li);
  }

  setActive(li) {
    // Fare kök listeden ayrılınca son gelinen kalır (mouseleave yalnızca bekleyen niyeti iptal eder)
    if (!li || li.classList.contains('is-active')) return;
    this.roots.forEach((r) => r.classList.toggle('is-active', r === li));
    const index = li.dataset.index;
    const own = this.images.find((img) => img.dataset.index === index);
    const fallback = this.images.find((img) => img.dataset.index === 'default');
    const show = own || fallback || null;
    this.images.forEach((img) => img.classList.toggle('is-active', img === show));
  }

  /* ---- mobil: alt panel ---- */

  openSub(li) {
    const sub = li.querySelector('[data-alv-sub]');
    if (!sub) return;
    this.activeSub = li;
    sub.setAttribute('open', '');
    li.querySelector('[data-alv-sub-open]').setAttribute('aria-expanded', 'true');
    this.classList.add('alv-menu--sub-open');
    history.pushState({ alvSub: true }, '');
    this.pushedState = true;
    const back = sub.querySelector('[data-alv-sub-close]');
    trapFocus(sub, back || sub);
  }

  requestCloseSub() {
    if (this.pushedState) {
      history.back(); // popstate → closeSub
    } else {
      this.closeSub();
    }
  }

  closeSub() {
    const li = this.activeSub;
    if (!li) return;
    const sub = li.querySelector('[data-alv-sub]');
    const opener = li.querySelector('[data-alv-sub-open]');
    sub.removeAttribute('open');
    opener.setAttribute('aria-expanded', 'false');
    this.classList.remove('alv-menu--sub-open');
    this.activeSub = null;
    this.pushedState = false;
    if (this.hasAttribute('open')) trapFocus(this.panel, opener);
  }

  onPopState(e) {
    if (e.state && e.state.alvSub) return;
    if (!this.activeSub) return;
    this.closeSub();
    if (this.closeAllAfterPop) this.finishClose();
  }
}

customElements.define('alv-menu', AlvMenu);
